import { onScopeDispose, watch } from "vue";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "@/mixins/firebase";
import { useAuthStore } from "@/stores/auth";

/** `last_seen` is written at most this often per user */
export const ACTIVITY_THROTTLE_MS = 60 * 60 * 1000;

export function shouldRecordActivity(
  lastSeen: Date | null,
  lastAttemptAt: number | null,
  now: number,
): boolean {
  // the tab's own attempt counts too, so a skewed clock or a failed write can't cause a write on every check
  if (lastAttemptAt !== null && now - lastAttemptAt < ACTIVITY_THROTTLE_MS) return false;
  if (lastSeen && now - lastSeen.getTime() < ACTIVITY_THROTTLE_MS) return false;
  return true;
}

/**
 * Keeps the user's `last_seen` up to date while they actually use the app: checked when the app
 * boots and whenever the tab becomes visible again, and written at most once per
 * `ACTIVITY_THROTTLE_MS`. Token refreshes and background tabs never count as activity.
 *
 * The throttle reads `last_seen` from the user-doc snapshot in the auth store, so it holds across
 * tabs and devices. Call once, from `App.vue`.
 */
export function useActivityTracker() {
  const authStore = useAuthStore();
  let lastAttemptAt: number | null = null;

  function recordActivity() {
    const uid = authStore.user?.uid;
    // `undefined` until the user doc has loaded; passcode sessions never load it
    const lastSeen = authStore.lastSeen;
    if (!uid || lastSeen === undefined || !authStore.is.loggedIn()) return;
    if (document.visibilityState !== "visible") return;

    const now = Date.now();
    if (!shouldRecordActivity(lastSeen, lastAttemptAt, now)) return;
    lastAttemptAt = now;

    // not awaited: a write only resolves once the server confirms it
    updateDoc(doc(db, "users", uid), { last_seen: serverTimestamp() }).catch(console.error);
  }

  function onVisibilityChange() {
    if (document.visibilityState === "visible") recordActivity();
  }

  // re-checks on sign-in and once the user doc has loaded, not on every snapshot
  watch(
    [() => authStore.user?.uid, () => authStore.lastSeen === undefined],
    ([uid], previous) => {
      // a different user signed in on this tab: their throttle starts fresh
      if (previous && uid !== previous[0]) lastAttemptAt = null;
      recordActivity();
    },
    { immediate: true },
  );
  document.addEventListener("visibilitychange", onVisibilityChange);
  onScopeDispose(() => document.removeEventListener("visibilitychange", onVisibilityChange));
}
